import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import SplitPane from 'react-split-pane';
import styled, { css } from 'styled-components';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { notification } from 'ui';
import { withRouter } from 'utils/withRouter';
import { viewModes, announcementBannerHeight } from 'utils/app-configs';
import { useSelector } from 'react-redux';
import Banner from 'common/banner/banner';
import PlanningBoardTop from './../containers/planning-board-top-container';
import PlanningBoardBottom from './../containers/planning-board-bottom-container';


const StyledWrapper = styled.div`
  position: relative;
  height: ${p => p.paneSize};

  ${p => p.fullScreen && p.hasAnnouncementBanner && css`
    margin-top: ${announcementBannerHeight}px;
  `}

  .Resizer {
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    background: #fff;
    z-index: 1;
    -moz-background-clip: padding;
    -webkit-background-clip: padding;
    background-clip: padding-box;

    &::after {
      content: '';
      width: 60px;
      background: #d9e0e6;
      height: 4px;
      display: block;
      border-radius: 2px;
      margin: auto;
    }

    &:hover {
      -webkit-transition: all 0.2s ease;
      transition: all 0.2s ease;
    }

    &.horizontal {
      height: 8px;
      padding-top: 1px;
      padding-bottom: 1px;
      margin: -5px 0;
      cursor: row-resize;
      width: 100%;
      border-top: 1px solid #d9e0e6;
      border-bottom: 1px solid #d9e0e6;
      z-index: 80;

      &:hover {
        height: 16px;
        border-top: 5px solid #d9e0e6;
        border-bottom: 5px solid #d9e0e6;
      }
    }
  }

  .SplitPane {
    width: calc(100% + 39px) !important;
    margin-left: -20px;
  }

  .Pane1,
  .Pane2 {
    background: #ecf2f6;
    flex-direction: inherit;
  }

  .Pane1 {
    height: ${p => p.pane1Size} !important;
    display: block;
    ${p => p.split && css`
      overflow: auto;
      overflow-x: hidden;
    `}
    ${p => p.isBottom && css`
      display: none !important;
    `}
  }

  .Pane2 {
    height: ${p => p.pane2Size} !important;
    ${p => p.split && css`
      overflow: auto;
    `}
    ${p => p.isTop && css`
      display: none !important;
    `}
  }
`;

const PlanningBoard = ({
  viewMode,
  fullScreen,
  setFullScreen,
}) => {
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [pane1Size, setPane1Size] = useState();
  const [pane2Size, setPane2Size] = useState();
  const [paneSize, setPaneSize] = useState();
  const handle = useFullScreenHandle();
  const splitRef = useRef();
  const { hasAnnouncementBanner } = useSelector(state => state.user);

  const setPanelSizes = () => {
    const fullScreenOffset = fullScreen ? 25 : 126;
    const hasBannerSize = hasAnnouncementBanner ? announcementBannerHeight : 0;
    if (viewMode === viewModes.TOP) {
      setPaneSize(`calc(100vh - ${fullScreenOffset + hasBannerSize}px)`);
      setPane1Size('100%');
      setPane2Size('0%');
    } else if (viewMode === viewModes.BOTTOM) {
      setPaneSize(`calc(100vh - ${fullScreenOffset + hasBannerSize}px)`);
      setPane1Size('0%');
      setPane2Size('100%');
    } else {
      setPaneSize(`calc(100vh - ${fullScreenOffset + hasBannerSize}px)`);
    }
  };

  useEffect(() => {
    setPanelSizes();
    // notification also provides a global config() method that can be used for specifying
    // the default options. Once this method is used, all the notification boxes will take into
    // account these globally defined options when displaying.
    notification.config({
      getContainer: () => document.getElementsByClassName('fullscreen')[0],
    });
    return () => {
      notification.config({
        getContainer: () => document.body,
      });
    };
  }, []);

  useEffect(() => {
    setPanelSizes();
  }, [viewMode, fullScreen]);

  useEffect(() => {
    if (fullScreen) {
      handle.enter();
    } else {
      handle.exit();
    }
  }, [fullScreen]);

  const handleOrderClick = (item) => {
    if (fullScreen) {
      setFullScreen(false);
    }
    if (item.uri) {
      window.open(
        `${window.location.origin}/${item.uri}`, '_blank',
      );
    }
  };

  return (
    <>
      <FullScreen handle={handle} onChange={value => setFullScreen(value)}>
        <DndProvider backend={HTML5Backend}>
          {
            fullScreen && hasAnnouncementBanner &&
            <Banner
              isFullScreen={fullScreen}
              setFullScreen={setFullScreen}
            />
          }
          <StyledWrapper
            fullScreen={fullScreen}
            hasAnnouncementBanner={hasAnnouncementBanner}
            pane1Size={viewMode !== viewModes.TOPBOTTOM && pane1Size}
            pane2Size={viewMode !== viewModes.TOPBOTTOM && pane2Size}
            split={viewMode === viewModes.TOPBOTTOM}
            isTop={viewMode === viewModes.TOP}
            isBottom={viewMode === viewModes.BOTTOM}
            paneSize={paneSize}
          >
            <SplitPane
              split='horizontal'
              defaultSize={viewMode === viewModes.TOPBOTTOM && '50%'}
              allowResize={viewMode === viewModes.TOPBOTTOM}
              style={{
                overflow: 'unset',
              }}
              resizerStyle={viewMode !== viewModes.TOPBOTTOM && {
                display: 'none',
              }}
              ref={splitRef}
            >
              <PlanningBoardTop
                fullScreen={fullScreen}
                selectedOrderIds={selectedOrderIds}
                handleOrderClick={handleOrderClick}
                split={viewMode === viewModes.TOPBOTTOM}
                visible={viewMode === viewModes.TOPBOTTOM || viewMode === viewModes.TOP}
                parent={splitRef}
              />
              <PlanningBoardBottom
                fullScreen={fullScreen}
                selectedOrderIds={selectedOrderIds}
                setSelectedOrderIds={setSelectedOrderIds}
                handleOrderClick={handleOrderClick}
                split={viewMode === viewModes.TOPBOTTOM}
              />
            </SplitPane>
          </StyledWrapper>
        </DndProvider>
      </FullScreen>
    </>
  );
};

PlanningBoard.propTypes = {
  viewMode: PropTypes.string.isRequired,
  fullScreen: PropTypes.bool.isRequired,
  setFullScreen: PropTypes.func.isRequired,
};

export default withRouter(PlanningBoard);
